import { useRef } from 'react';

import * as styles from './FileUploadCard.css.ts';

interface FileUploadCardProps {
  isActive?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  hasError?: boolean;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileSelect: (file: File) => void;
  onPaste: (event: React.ClipboardEvent) => void;
}

/**
 * FileUploadCard - Simplified file upload component for tabbed interface
 * TODO: Add support for images (PNG, JPG, GIF) and other audio formats (MP3, M4A)
 */
export function FileUploadCard({
  isActive: _isActive = false,
  fileInputRef,
  hasError: _hasError = false,
  onFileInputChange,
  onFileSelect,
  onPaste,
}: FileUploadCardProps) {
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className={styles.container}>
      <div
        ref={dropZoneRef}
        aria-label="Upload file by clicking or dropping files here"
        className={styles.dropZone}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
      >
        <div className={styles.uploadIcon}>
          <svg fill="none" height="48" viewBox="0 0 24 24" width="48" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 15.01L9.41 16.42L11 14.84V19H13V14.84L14.59 16.43L16 15.01L12.01 11L8 15.01Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div className={styles.uploadText}>
          <div className={styles.primaryText}>Drop files here or click to upload</div>
          <div className={styles.secondaryText}>Supports WAV and OPUS audio files</div>
        </div>
      </div>

      <input ref={fileInputRef} accept=".wav,.opus" style={{ display: 'none' }} type="file" onChange={onFileInputChange} />
    </div>
  );
}
